import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import ProductCatalog from './components/ProductCatalog';
import POSCheckout from './components/POSCheckout';
import InventoryManagement from './components/InventoryManagement';
import CategoryManager from './components/CategoryManager';
import PaymentConfigModal from './components/PaymentConfigModal';
import { posService } from '../../lib/posService';
import { gymService } from '../../lib/services';
import AIHelpAssistant from '../../components/ui/AIHelpAssistant';

const POSInventoryManagement = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('pos');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedGym, setSelectedGym] = useState(null);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaymentConfigOpen, setIsPaymentConfigOpen] = useState(false);
  const [creditCardConfigs, setCreditCardConfigs] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedGym) {
      loadProducts();
      loadCategories();
      loadCreditCardConfigs();
    }
  }, [selectedGym]);

  const loadInitialData = async () => {
    setLoading(true);
    const { data: gymsData } = await gymService?.getActiveGyms();
    if (gymsData && gymsData?.length > 0) {
      setGyms(gymsData);
      setSelectedGym(gymsData?.[0]?.id);
    }
    setLoading(false);
  };

  const loadProducts = async () => {
    const { data } = await posService?.getProducts(selectedGym);
    if (data) {
      setProducts(data);
    }
  };

  const loadCategories = async () => {
    const { data } = await posService?.getCategories(selectedGym);
    if (data) {
      setCategories(data);
    }
  };

  const loadCreditCardConfigs = async () => {
    const { data } = await posService?.getCreditCardConfigs(selectedGym);
    if (data) {
      setCreditCardConfigs(data);
    }
  };

  const handleAddToCart = (product) => {
    // Check if product is out of stock
    if (product?.stock_quantity === 0) {
      alert(`${product?.name} is out of stock`);
      return;
    }

    setCart(prev => {
      const existing = prev?.find(item => item?.id === product?.id);
      if (existing) {
        // Check if adding one more would exceed stock
        if (existing?.quantity + 1 > product?.stock_quantity) {
          alert(`Cannot add more. Only ${product?.stock_quantity} units available for ${product?.name}`);
          return prev;
        }
        return prev?.map(item =>
          item?.id === product?.id
            ? { ...item, quantity: item?.quantity + 1 }
            : item
        );
      }
      return [...prev, { 
        ...product, 
        quantity: 1,
        stock_quantity: product?.stock_quantity,
        low_stock_threshold: product?.low_stock_threshold
      }];
    });
  };

  const handleUpdateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev?.filter(item => item?.id !== productId));
    } else {
      setCart(prev => prev?.map(item => {
        if (item?.id === productId) {
          // Check stock availability
          if (item?.stock_quantity !== undefined && quantity > item?.stock_quantity) {
            alert(`Cannot add more. Only ${item?.stock_quantity} units available for ${item?.name}`);
            return item;
          }
          return { ...item, quantity };
        }
        return item;
      }));
    }
  };

  const handleRemoveFromCart = (productId) => {
    setCart(prev => prev?.filter(item => item?.id !== productId));
  };

  const handleClearCart = () => {
    setCart([]);
  };

  const handleCheckout = async (paymentData) => {
    if (!selectedGym || cart?.length === 0) return { error: { message: 'Cart is empty' } };

    const subtotal = cart?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
    const tax = subtotal * 0.08; // 8% tax
    const total = subtotal + tax;

    const transactionData = {
      payment_method: paymentData?.paymentMethod,
      subtotal: subtotal,
      tax: tax,
      total: total,
      member_id: paymentData?.memberId || null,
      credit_card_last4: paymentData?.cardLast4 || null,
      notes: paymentData?.notes || null
    };

    const items = cart?.map(item => ({
      product_id: item?.id,
      product_name: item?.name,
      quantity: item?.quantity,
      unit_price: item?.price,
      subtotal: item?.price * item?.quantity
    }));

    const { data, error } = await posService?.createTransaction(selectedGym, transactionData, items);
    
    if (!error) {
      setCart([]);
      await loadProducts(); // Refresh products to update stock
    }

    return { data, error };
  };

  const handleBarcodeSearch = async (barcode) => {
    const { data, error } = await posService?.getProductByBarcode(selectedGym, barcode);
    if (!error && data) {
      handleAddToCart(data);
      return { success: true };
    }
    return { success: false, error };
  };

  const handleALUSearch = async (alu) => {
    const { data, error } = await posService?.getProductByALU(selectedGym, alu);
    if (!error && data) {
      handleAddToCart(data);
      return { success: true };
    }
    return { success: false, error };
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                POS & Inventory Management
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Complete point-of-sale and inventory control system
              </p>
            </div>
            {(userProfile?.role === 'owner' || userProfile?.role === 'sysadmin') && (
              <Button
                onClick={() => setIsPaymentConfigOpen(true)}
                iconName="Settings"
                iconPosition="left"
                variant="outline"
              >
                Payment Settings
              </Button>
            )}
          </div>

          {/* Gym Selector */}
          {gyms?.length > 1 && (
            <div className="bg-card rounded-xl p-4 shadow-md border border-border mb-6">
              <div className="flex items-center gap-4">
                <Icon name="Building" size={20} color="var(--color-muted-foreground)" />
                <div className="flex gap-2 flex-wrap">
                  {gyms?.map(gym => (
                    <button
                      key={gym?.id}
                      onClick={() => setSelectedGym(gym?.id)}
                      className={`
                        px-4 py-2 rounded-lg text-sm font-medium transition-colors
                        ${selectedGym === gym?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                        }
                      `}
                    >
                      {gym?.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-card rounded-xl shadow-md border border-border mb-6">
            <div className="flex border-b border-border overflow-x-auto">
              {[
                { id: 'pos', label: 'Point of Sale', icon: 'ShoppingCart' },
                { id: 'inventory', label: 'Inventory', icon: 'Package' },
                { id: 'categories', label: 'Categories', icon: 'FolderOpen' }
              ]?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap
                    ${activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          {activeTab === 'pos' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <ProductCatalog
                  products={products}
                  categories={categories}
                  onAddToCart={handleAddToCart}
                  onBarcodeSearch={handleBarcodeSearch}
                  onALUSearch={handleALUSearch}
                  loading={loading}
                />
              </div>
              <div className="lg:col-span-1">
                <POSCheckout
                  cart={cart}
                  onUpdateQuantity={handleUpdateCartQuantity}
                  onRemoveItem={handleRemoveFromCart}
                  onClearCart={handleClearCart}
                  onCheckout={handleCheckout}
                  creditCardConfigs={creditCardConfigs}
                />
              </div>
            </div>
          )}

          {activeTab === 'inventory' && (
            <InventoryManagement
              products={products}
              categories={categories}
              gymId={selectedGym}
              onProductsChange={loadProducts}
            />
          )}

          {activeTab === 'categories' && (
            <CategoryManager
              categories={categories}
              gymId={selectedGym}
              onCategoriesChange={loadCategories}
            />
          )}
        </div>
      </div>
      {isPaymentConfigOpen && (
        <PaymentConfigModal
          onClose={() => setIsPaymentConfigOpen(false)}
          gymId={selectedGym}
          configs={creditCardConfigs}
          onConfigsChange={loadCreditCardConfigs}
          onConfigSaved={loadCreditCardConfigs}
        />
      )}

      {/* AI Help Assistant */}
      <AIHelpAssistant
        pageContext="POS & Inventory Management"
        pageName="POS & Inventory"
      />
    </div>
  );
};

export default POSInventoryManagement;